import { ALL_PRODUCT_TYPES } from "constants/chart.constants";

import { BOE_DISPLAY_NAMES } from "./productNameHelpers";

// What the chart service called the two original BOE products before the gas ratio
// was part of the name. A payload built by the previous chart service — or one served
// from a chart-redis entry that has not been reloaded yet — still carries these, so
// both the current label and the legacy one have to resolve to the same product.
const LEGACY_BOE_SERIES_LABELS: Record<string, string> = {
  BOE: "Wellhead BOE",
  "Sales BOE": "Sales BOE"
};

// Derived from BOE_DISPLAY_NAMES so the picker label and the series label cannot
// drift apart.
const BOE_SERIES_LABEL_ALIASES = Object.fromEntries(
  Object.entries(BOE_DISPLAY_NAMES).map(([key, label]): [string, string | string[]] => [
    key,
    Object.hasOwn(LEGACY_BOE_SERIES_LABELS, key)
      ? [label, LEGACY_BOE_SERIES_LABELS[key]]
      : label
  ])
);

// Some products have alternate series label representations generated by the backend.
// Key is the internal product key (stored in productLineStyles); value is the label,
// or the list of labels, the backend uses in series names.
export const SERIES_LABEL_ALIASES: Record<string, string | string[]> = {
  "Total Fluid": "Total (O+C+W)",
  IWB: "Inst Water Balance",
  CWB: "Cum Water Balance",
  // The BOE family is labelled with its gas ratio by the backend chart service
  ...BOE_SERIES_LABEL_ALIASES,
  // C5 family: backend uses ASCII "C5+" while the internal key is "C5"
  C5: "C5+",
  "Oil+Cond+C5": "Oil, Cond, C5+",
  "C5 Yields Raw": "C5+ Yields Raw",
  "C5 Yields Sales": "C5+ Yields Sales",
  "Oil+Cond+C5 Yields Raw": "Oil, Cond, C5+ Yields Raw",
  "Oil+Cond+C5 Yields Sales": "Oil, Cond, C5+ Yields Sales",
  // CO₂ injection: backend uses ASCII "CO2" and full "Injection"
  "CO2 Inj": "CO2 Injection",
  // Injection products: backend uses full "Injection", keys use abbreviated "Inj"
  "Water Inj": "Water Injection",
  "Gas Inj": "Gas Injection",
  "Steam Inj": "Steam Injection",
  "Polymer Inj": "Polymer Injection",
  "Acid Gas Inj": "Acid Gas Injection",
  "Solvent Inj": "Solvent Injection",
  "Total Liquid Inj": "Total Liquid Injection",
  "Total Gas Inj": "Total Gas Injection"
};

/**
 * Every backend series label registered for a product key, current form first.
 * Empty when the product has no alias.
 */
export function getSeriesLabelAliases(productKey: string): string[] {
  if (!productKey || !Object.hasOwn(SERIES_LABEL_ALIASES, productKey)) return [];
  const alias = SERIES_LABEL_ALIASES[productKey];
  return Array.isArray(alias) ? alias : [alias];
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Cache compiled RegExp objects keyed by the product string to avoid repeated allocations
// in tight loops (e.g., per-series × per-product inside translator functions).
const parenRegExpCache = new Map<string, RegExp>();

function getParenRegExp(product: string): RegExp {
  let cached = parenRegExpCache.get(product);
  if (!cached) {
    cached = new RegExp(`\\(${escapeRegExp(product)}\\)`);
    parenRegExpCache.set(product, cached);
  }
  return cached;
}

/**
 * Returns true if `text` contains `product` wrapped in parentheses.
 * Prevents partial matches like "Oil" matching "Bluesky Oil" or "Gas" matching "Shallow Gas".
 * e.g. "(Oil)" or "((O+C)GR)" match, but not "Bluesky Oil".
 */
function containsProductInParens(text: string | undefined, product: string): boolean {
  if (!text) return false;
  return getParenRegExp(product).test(text);
}

/**
 * Checks whether a series (identified by its label and groupTitle) belongs to a given product.
 *
 * Handles mismatches between the product display label (e.g. 'C₂') stored in multiProduct
 * properties and the key form (e.g. 'C2') or backend alias (e.g. 'Inst Water Balance') used
 * in series labels.
 *
 * @param series - The series to check, with optional label and groupTitle.
 * @param product - The product display label to match against (e.g. from secondaryProducts).
 */
export function seriesMatchesProduct(
  series: { label?: string; groupTitle?: string },
  product: string,
  options?: { exactAlias?: boolean }
): boolean {
  // Resolve the product entry by display label first, then by key.
  // This handles both directions:
  //   "Oil + Cond" (label) → key "Oil"
  //   "Oil" (key)          → label "Oil + Cond"
  const allProducts = Object.values(ALL_PRODUCT_TYPES) as any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  const productEntry =
    allProducts.find((p) => p.label === product) ??
    allProducts.find((p) => p.key === product);

  const productKey: string = productEntry?.key ?? product;
  const productLabel: string = productEntry?.label ?? product;

  const seriesAliases = getSeriesLabelAliases(productKey);

  return (
    containsProductInParens(series.groupTitle, product) ||
    containsProductInParens(series.label, product) ||
    containsProductInParens(series.groupTitle, productKey) ||
    containsProductInParens(series.label, productKey) ||
    containsProductInParens(series.groupTitle, productLabel) ||
    containsProductInParens(series.label, productLabel) ||
    seriesAliases.some(
      (alias) =>
        (options?.exactAlias === true &&
          (series.label === alias || series.groupTitle === alias)) ||
        containsProductInParens(series.groupTitle, alias) ||
        containsProductInParens(series.label, alias)
    )
  );
}
